import React, {useEffect, useRef, useState} from "react";
import AIModelSelect from "./components/AIModelSelect";
import { Helmet } from "react-helmet-async";
import PromptTemplateSelect from "./components/PromptTemplateSelect";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { Card, Form } from "antd";
const App: React.FC = () => {
    const [form] = Form.useForm();
    type RequestAICompletion = {
        keywords: string,
        tone: string,
        length: string,
        modelAI: string,
        typeAI: string,
        customPrompt?: string,
    };
    const [inputValue, setInputValue] = useState<string>("");
    const [tone, setTone] = useState<string>("chuyên nghiệp");
    const [wordCount, setWordCount] = useState<number>(1000);
    const [modelAI, setModelAI] = useState("");
    const [typeAI, setTypeAI] = useState("");
    const [customPrompt, setCustomPrompt] = useState(""); // Prompt tùy ý
    const [copied, setCopied] = useState(false);
    const [generationError, setGenerationError] = useState("");

    const [streamedContent, setStreamedContent] = useState<string>("");
    const bufferRef = useRef<string>(""); // lưu text stream đang collect từ backend
    const intervalRef = useRef<NodeJS.Timeout | null>(null); // để quản lý worker

    const [stats, setStats] = useState({
        wordCount: 0,
        seoCount: 0,
        sentenceCount: 0,
        paragraphCount: 0,
    });

    // The default keeps the frontend and API on the same domain. Set
    // REACT_APP_API_URL before building only when the API is on another host.
    const apiURL = process.env.REACT_APP_API_URL || "/api/v1/generate-content";
    const URLs: { [key: string]: string } = {
        // Both providers use the same endpoint; the selected model is in the body.
        "OpenAI": apiURL,
        "GroqAI": apiURL,
    };


    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const handleModelAI = (modelAI: string, typeAI: string) => {
        setModelAI(modelAI);
        setTypeAI(typeAI);
    }

    const handlePromptTemplate = (prompt: string) => {
        setCustomPrompt(prompt);
    }

    const handleInputWordCount = (e: React.ChangeEvent<HTMLInputElement>) => {
        if(Number(e.target.value) > 2000){
            // setWordCount(2000);
            // e.target.value = "2000";
            alert ("Số từ tối đa là 2000");
            return
        }
        setWordCount(Number(e.target.value));
    }

    const handleSubmitForm = async () => {
        //validate
        if(!typeAI){
            alert("Vui lòng chọn AI model");
            return;
        }
        setStreamedContent("");
        bufferRef.current = "";
        setGenerationError("");
        if (!inputValue && !customPrompt) return;

        const requestBody:RequestAICompletion = {
            keywords: inputValue,
            tone: tone,
            length: wordCount + " từ",
            modelAI: modelAI,
            typeAI: typeAI,
            customPrompt: customPrompt,
        };

        try {
            await collectStreamFromBackend(requestBody);
            startTypingWorker(10);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Không thể tạo bài viết. Vui lòng thử lại.";
            setGenerationError(message);
        } finally {
            // setInputValue("");
        }

    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(streamedContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // reset trạng thái sau 2 giây
        } catch (err) {
            console.error("Copy failed:", err);
        }
    };

    const collectStreamFromBackend = async (requestBody: RequestAICompletion) => {
        const url:string = URLs[requestBody.typeAI];
        console.info("[Content Generator] Sending request", {
            provider: requestBody.typeAI,
            model: requestBody.modelAI,
            endpoint: url,
        });
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody)
        });

        const result = await response.json().catch(() => null);
        console.info("[Content Generator] Response", {
            status: response.status,
            ok: response.ok,
        });
        if (!response.ok) {
            console.error("[Content Generator] API error", result);
            throw new Error(result?.error || "Không thể gửi yêu cầu tới dịch vụ AI.");
        }
        if (!result?.content) {
            console.error("[Content Generator] Empty AI response", result);
            throw new Error(result?.error || "Dịch vụ AI không trả về nội dung.");
        }
        bufferRef.current = result.content;
    };

    const startTypingWorker = (interval = 20) => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            if (bufferRef.current.length > 0) {
                const nextChar = bufferRef.current[0];
                bufferRef.current = bufferRef.current.slice(1);

                setStreamedContent(prev => {
                    const updated = prev + nextChar;

                    // Cập nhật thống kê
                    const words = updated.trim().split(/\s+/).filter(Boolean);
                    const sentences = updated.split(/[.!?]+/).filter(s => s.trim().length > 0);
                    const paragraphs = updated.split(/\n\s*\n/).filter(p => p.trim().length > 0);

                    // Đếm số lần xuất hiện của keywords
                    const keywordList = inputValue.toLowerCase().split(",").map(k => k.trim());
                    let seoCount = 0;
                    const lowerText = updated.toLowerCase();

                    for (let keyword of keywordList) {
                        if (keyword) {
                            const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
                            const matches = lowerText.match(regex);
                            seoCount += matches ? matches.length : 0;
                        }
                    }

                    setStats({
                        wordCount: words.length,
                        sentenceCount: sentences.length,
                        paragraphCount: paragraphs.length,
                        seoCount: seoCount,
                    });

                    return updated;
                });
            }
        }, interval);
    };

    return (
        <>
            <Helmet>
                <title>Công Cụ Viết Content Bằng AI Chuẩn SEO Tạo Content Hay, Sáng Tạo</title>
                <meta name="description" content="AI viết content sử dụng trí tuệ nhân tạo như GPT-3, GPT-4, ChatGPT, Gemini, Claude..." />
            </Helmet>

            <Header />
            <main className="generator-layout">
                <section
                    className="generator-hero"
                    style={{
                        background: "linear-gradient(90deg, #a1c4fd 0%, #c2e9fb 50%, #d4a6f9 100%)",
                        textAlign: "center",
                        padding: "10px",
                        height: "300px",
                    }}
                >
                    <h1 className="text-4xl font-bold text-white drop-shadow">Content generator by AI</h1>
                </section>
                <section className="generator-input">
                    <Card
                        className="generator-panel"
                        title="Thống kê model AI"
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            width: "80%",
                            margin: "0 auto",
                            textAlign: "center",
                        }}
                    >
                        <section>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
                                {[
                                        { label: "GPT 4.0 mini", total: 999999 },
                                        { label: "GPT 4.5", total: 999999 },
                                        { label: "Llama", total: 999999 },
                                        { label: "Deepseek", total: 999999 },
                                        { label: "Gamma", total: 999999 },
                                        { label: "Others", total: 999999 },
                                    ].map((item, index) => (
                                        <Card
                                        key={index}
                                        size="small"
                                        style={{
                                            width: "120px",
                                            textAlign: "center",
                                            border: "1px solid #e6f4ff",
                                            backgroundColor: "#f5fbff",
                                            cursor: "pointer",
                                        }}
                                        >
                                            <div style={{ fontWeight: "bold", color: "#1677ff" }}>{item.label}</div>
                                            <div style={{ fontSize: 12 }}>{item.total.toLocaleString()}</div>
                                        </Card>
                                ))
                                }
                            </div>
                        </section>
                        <div>
                            {/*// draw a grey line */}
                            <hr style={{ margin: "16px 0 8px", border: 0, borderTop: "1px solid #999" }} />
                        </div>
                        <section>
                            <AIModelSelect onChange={handleModelAI} />
                            <Form
                                form={form}
                                layout="vertical"
                                initialValues={{ length: 1000, tone: "chuyên nghiệp" }}
                                style={{ width: "100%", margin: "0 auto" }}
                                onFinish={handleSubmitForm}
                            >
                                <Form.Item
                                    label="Nhập từ khoá"
                                    name="keywords"
                                    rules={[{ required: true, message: "Vui lòng nhập từ khoá" }]}
                                >
                                    <input
                                        type="text"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        placeholder="Từ khoá"
                                        style={{ padding: "10px", width: "100%", fontSize: "16px", borderRadius: "5px", border: "1px solid #ccc" }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Độ dài bài viết"
                                    name="length"
                                    rules={[{ required: true, message: "Vui lòng nhập độ dài bài viết" }]}
                                >
                                    <input
                                        type="number"
                                        placeholder="Nhập số từ"
                                        // value={wordCount}
                                        onChange={handleInputWordCount}
                                        style={{ padding: "10px", width: "100%", fontSize: "16px", borderRadius: "5px", border: "1px solid #ccc" }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Tone"
                                    name="tone"
                                    rules={[{ required: false, message: "Vui lòng chọn tone" }]}
                                >
                                    <select
                                        onChange={(e) => setTone(e.target.value)}
                                        style={{ padding: "10px", fontSize: "16px", width: "100%", borderRadius: "5px", border: "1px solid #ccc" }}
                                    >
                                        <option value="thân thiện">Thân thiện</option>
                                        <option value="chuyên nghiệp">chuyên nghiệp</option>
                                        <option value="tự tin">Tự tin</option>
                                        <option value="chân thành">Chân thành</option>
                                        <option value="hài hước">Hài hước</option>
                                        <option value="truyền cảm hứng">Truyền cảm hứng</option>
                                        <option value="trang trọng">Trang trọng</option>
                                        <option value="nghiêm túc">Nghiêm túc</option>
                                        <option value="hướng dẫn">Hướng dẫn</option>
                                    </select>
                                </Form.Item>
                                <Form.Item
                                    label="Prompt tùy chỉnh"
                                    name="customPrompt"
                                    rules={[{ required: false, message: "Nhập prompt nếu muốn tinh chỉnh AI" }]}
                                >
                                    <PromptTemplateSelect onChange={handlePromptTemplate}/>
                                    <textarea
                                        value={customPrompt}
                                        onChange={(e) => setCustomPrompt(e.target.value)}
                                        rows={4}
                                        style={{ padding: "10px", width: "100%", fontSize: "16px", borderRadius: "5px", border: "1px solid #ccc" }}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: "10px 15px",
                                            fontSize: "16px",
                                            width: "100%",
                                            backgroundColor: "#007BFF",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        Gửi
                                    </button>
                                </Form.Item>
                                {generationError && (
                                    <p role="alert" style={{ color: "#d4380d", marginTop: "-12px" }}>
                                        {generationError}
                                    </p>
                                )}
                            </Form>
                        </section>
                    </Card>
                </section>
                <section className="generator-output">
                    <Card
                        className="generator-panel"
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            width: "80%",
                            margin: "0 auto",
                            textAlign: "center",
                        }}

                        title="Thống kê bài viết"

                    >
                        <div style={{
                            display: "flex",
                            gap: "15px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            marginTop: "15px"
                        }}>
                            {[
                                { label: "📝 Số từ", value: stats.wordCount },
                                { label: "💬 Số câu", value: stats.sentenceCount },
                                { label: "📄 Số đoạn", value: stats.paragraphCount },
                                { label: "🔍 Từ khóa", value: stats.seoCount },
                            ].map((item, index) => (
                                <div key={index} style={{
                                    background: "#f0f0f0",
                                    borderRadius: "8px",
                                    padding: "10px 15px",
                                    minWidth: "120px",
                                    textAlign: "center",
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)"
                                }}>
                                    <strong>{item.label}</strong><br />
                                    <span>{item.value}</span>
                                </div>
                            ))}
                        </div>
                        <hr style={{ marginTop: "20px" }} />
                        {streamedContent && (
                            <div
                                style={{
                                    textAlign: "left",
                                    background: "#f9f9f9",
                                    padding: "20px",
                                    borderRadius: "8px",
                                    position: "relative"
                                }}
                            >
                                <h4 style={{ textAlign: "center", color: "#333" }}>Nội dung bài viết</h4>
                                <button
                                    onClick={handleCopy}
                                    style={{
                                        position: "absolute",
                                        top: "15px",
                                        right: "15px",
                                        backgroundColor: "#007bff",
                                        color: "white",
                                        border: "none",
                                        padding: "8px 12px",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                        fontSize: "12px"
                                    }}
                                >
                                    {copied ? "Đã chép!" : "Copy HTML"}
                                </button>
                                <div dangerouslySetInnerHTML={{ __html: streamedContent }} />
                            </div>
                        )}
                    </Card>
                </section>

            </main>
            <Footer/>
        </>
    );
};

export default App;
